/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable no-param-reassign */
import { datadogRum } from '@datadog/browser-rum'
import {
  createAsyncThunk,
  createSlice,
  Draft,
  PayloadAction,
} from '@reduxjs/toolkit'
import {
  SessionDataArgs,
  ICheckoutRules,
} from '../../../graphQL/queries/getSession/types'
import {
  DefaultCheckoutRulesNames,
  PREFILL_CART_MAX_LIMIT,
} from '../../../consts/defaultConfigValues/defaultConstants'
import {
  resolveHidePrices,
  isHidePricesMisconfigured,
} from '../../../utils/hidePrices'
import { isPackageSortMisconfigured } from '../../../utils/packageSort'
import { isRecommendationLimitsMisconfigured } from '../../../utils/recommendationLimits'
import {
  getPrefillCartLimitIssue,
  PrefillCartLimitIssue,
} from '../../../utils/prefillCartLimit'
import { findMisplacedCartCheckoutSummaries } from '../../../utils/cartCheckoutSummaryPlacement'
import {
  IConfig,
  IModals,
  ISliceDefault,
  AlertConstrains,
  LoaderConfig,
  LoadersType,
  ErrorMessageTranslation,
  IAlert,
  IGetConfigArgs,
} from '../../../interfaces'
import {
  IModalActionsProps,
  IDialogActionsProps,
} from '../../../interfaces/actions'
import getConfig from '../../../services/getConfig'
import processConfig from '../../../utils/processConfig'
import { generateDataDogContext } from '../../../utils/dataDog'
import castSessionNotifications from '../../../utils/castNotifications'
import getUrlParams from '../../../utils/getUrlParams'
import flatTexts from '../../../utils/flatTexts'
import { writeCachedTheme } from '../../../utils/bootTheme'

export const initialState: ISliceDefault = {
  loaders: [],
  loading: true,
  config: undefined,
  loadingDelay: 1000,
  sessionId: undefined,
  dialogs: undefined,
  storeName: undefined,
  configRequestId: undefined,
  validators: [],
  currentModalItem: '',
  errorMessage: null,
  globalError: false,
  showErrorTitle: false,
  onErrorCallback: undefined,
  excludedUiComponents: [],
  cartAlerts: {
    [AlertConstrains.cartConditionPendingMinAmount]: {
      isActive: false,
    },
    [AlertConstrains.cartConditionPendingMinQty]: {
      isActive: false,
    },
    [AlertConstrains.cartConditionPendingMaxAmount]: {
      isActive: false,
    },
    [AlertConstrains.cartConditionPendingMaxQty]: {
      isActive: false,
    },
    [AlertConstrains.creditLimitMaxAmount]: {
      isActive: false,
    },
  },
  params: {},
  showRecommendations: false,
  showSuggestedItems: false,
  alerts: [],
  sessionNotifications: {
    STOCK_AVAILABLE: false,
    AUTHENTICATION_NEEDED: false,
    SESSION_CREATED: false,
    ORDER_REMINDER: false,
    ABANDONED_CART: false,
  },
}

/**
 * Whether `requestId` belongs to a `fetchConfig` that a later one has
 * replaced. Unset means no load has gone through `pending` yet, so a
 * hand-dispatched `fetchConfig.fulfilled` (as some tests build) lands. Once a
 * real load has run `pending`, a hand-built `fulfilled` whose id does not
 * match, or that carries no `meta`, is dropped like any superseded load.
 */
const isSupersededConfigLoad = (
  state: Pick<ISliceDefault, 'configRequestId'>,
  requestId: string | undefined,
) => state.configRequestId !== undefined && state.configRequestId !== requestId

export const defaultSlice = createSlice({
  name: 'defaultSlice',
  initialState,
  reducers: {
    manageErrors: (
      state,
      {
        payload: {
          errorMessageTranslation,
          globalError,
          showErrorTitle,
          onErrorCallback,
        },
      }: PayloadAction<{
        errorMessageTranslation: ErrorMessageTranslation
        globalError?: boolean
        showErrorTitle?: boolean
        onErrorCallback?: () => void
      }>,
    ) => {
      let errorMessage: string = 'An error has occurred'
      const errorsTexts = flatTexts(state.config?.texts?.errors)
      let callback
      if (errorMessageTranslation) {
        switch (errorMessageTranslation) {
          case ErrorMessageTranslation.CatalogueEmptyData:
            errorMessage =
              (errorsTexts?.catalogueEmptyData as string) || 'Catalogue empty'
            break
          case ErrorMessageTranslation.InitialData:
            errorMessage =
              (errorsTexts?.initialData as string) || 'Session expired'
            break
          case ErrorMessageTranslation.GetCart:
            errorMessage =
              (errorsTexts?.getCart as string) || 'Error on get cart'
            break
          case ErrorMessageTranslation.NonProvidedParams:
            errorMessage =
              (errorsTexts?.nonProvidedParams as string) || 'No params provided'
            break
          case ErrorMessageTranslation.ErrorOnInitialLoad:
            errorMessage =
              errorsTexts?.tryAgainIntruction || 'An error has ocurred'
            callback = onErrorCallback
            break
          default:
            break
        }
      }
      state.onErrorCallback = callback
      state.errorMessage = errorMessage
      state.globalError = !!globalError
      state.showErrorTitle = !!showErrorTitle
    },
    addLoader: (state, action: PayloadAction<LoaderConfig>) => {
      const { payload } = action
      if (typeof payload === 'string') {
        state.loaders = [...state.loaders, payload]
      } else {
        state.loaders = [...state.loaders, payload.name]
        state.loadingDelay = payload.delay
      }
      state.loading = true
    },
    removeLoader: (state, action: PayloadAction<string>) => {
      const index = state.loaders.indexOf(action.payload)
      const newLoaders = [...state.loaders]
      newLoaders.splice(index, 1)
      state.loaders = newLoaders
      state.loading = newLoaders.length > 0
    },
    setSessionData: (state, action: PayloadAction<SessionDataArgs>) => {
      const { payload } = action
      if (
        !payload.configuration?.checkoutRules?.cartConditionPendingMinAmount
          .isActive
      ) {
        state.excludedUiComponents = [
          DefaultCheckoutRulesNames.MIN_AMOUNT_CART_CHECKOUT_RULE,
        ]
      }
      if (payload?.configuration?.notifications) {
        state.sessionNotifications = castSessionNotifications(
          payload?.configuration?.notifications,
        )
      }
      state.sessionData = payload
      if (state.config?.options) {
        state.config.options.hidePricesActive = resolveHidePrices(
          state.config.options.hidePrices,
          payload.customer?.customFields,
        )
      }
      const { frequentlyBoughtTogether, suggestedItems } =
        payload.configuration?.recommendedProducts || {}
      const showRecommendations = !!payload.configuration?.showRecommendations
      const showFrequentlyBoughtTogetherItems =
        frequentlyBoughtTogether === 'ML' || showRecommendations
      const showSuggestedItems = suggestedItems === 'ML' || showRecommendations
      state.showRecommendations = showFrequentlyBoughtTogetherItems
      state.showSuggestedItems = showSuggestedItems
    },
    setCheckoutRules: (state, { payload }: PayloadAction<ICheckoutRules>) => {
      if (state?.sessionData?.configuration?.checkoutRules && payload) {
        state.sessionData.configuration.checkoutRules = {
          ...state.sessionData.configuration.checkoutRules,
          ...payload,
        }
      }
    },
    setSessionId: (state, { payload }) => {
      state.sessionId = payload
    },
    updateParams: (state, { payload }) => {
      state.params = payload
    },
    openDialog: (state, action: PayloadAction<IDialogActionsProps>) => {
      const {
        icon,
        title,
        message,
        buttons,
        id,
        details,
        hidePrices,
        currency,
        currencyCode,
      } = action.payload
      /*
       * Assigned whole rather than merged, so the four money-and-rows props a
       * previous dialog set cannot survive into the next one. `closeDialog`
       * rebuilds this object the same way and therefore clears them too.
       */
      state.dialogs = {
        show: true,
        icon: icon || { name: '' },
        title: title || { value: '' },
        message: message || { value: '' },
        buttons,
        id,
        details,
        hidePrices,
        currency,
        currencyCode,
      }
    },
    closeDialog: (state, action: PayloadAction<IDialogActionsProps>) => {
      state.dialogs = {
        show: false,
        icon: { name: '' },
        title: { value: '' },
        message: { value: '' },
        buttons: [],
      }
    },
    openModal: (state, action: PayloadAction<IModalActionsProps>) => {
      if (state.modals) {
        const { container, state: modalState = {}, item = '' } = action.payload
        state.currentModalItem = item
        state.modals[container] = {
          show: true,
          state: modalState,
        }
      }
    },
    closeModal: (state, action: PayloadAction<IModalActionsProps>) => {
      if (state.modals) {
        const { container, state: modalState } = action.payload
        if (container === 'all') {
          Object.keys(state.modals).forEach((modalKey) => {
            if (state.modals) {
              state.modals[modalKey] = {
                ...state.modals[modalKey],
                show: false,
              }
            }
          })
        } else {
          state.modals[container] = {
            show: false,
            state: modalState || state.modals[container]?.state,
          }
        }
      }
    },
    setModals: (state, { payload }) => {
      state.modals = payload
    },
    setStoreName: (state, action: PayloadAction<string>) => {
      state.storeName = action.payload
    },
    setValidators: (state, action: PayloadAction<string[]>) => {
      state.validators = action.payload
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.secondaryLoader = action.payload
    },
    setErrorMessage: (state, action: PayloadAction<string>) => {
      state.errorMessage = action.payload
    },
    setCartAlerts: (state, action) => {
      state.cartAlerts = action.payload
    },
    addAlert: (state, { payload }: PayloadAction<IAlert>) => {
      const key = new Date().getTime().toString()
      state.alerts = [...state.alerts, { ...payload, key }] as Draft<IAlert>[]
    },
    removeAlert: (state, { payload }: PayloadAction<void>) => {
      state.alerts.shift()
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchConfig.pending, (state, action) => {
      state.configRequestId = action.meta.requestId
    })
    builder.addCase(fetchConfig.fulfilled, (state, action) => {
      // A slower load for a storefront the shopper has since left must not
      // render its config under the newer URL. `meta` is optional-chained
      // because a hand-built action (in tests) may carry none.
      if (isSupersededConfigLoad(state, action.meta?.requestId)) {
        return
      }
      state.config = action.payload as Draft<IConfig>
      if (state.config?.options) {
        state.config.options.hidePricesActive = resolveHidePrices(
          state.config.options.hidePrices,
          state.sessionData?.customer?.customFields,
        )
      }
    })
  },
})

export const {
  setSessionId,
  updateParams,
  openModal,
  closeModal,
  openDialog,
  closeDialog,
  setModals,
  setStoreName,
  setSessionData,
  setValidators,
  setLoading,
  setErrorMessage,
  setCartAlerts,
  addLoader,
  removeLoader,
  manageErrors,
  addAlert,
  removeAlert,
  setCheckoutRules,
} = defaultSlice.actions

/**
 * One entry per `PrefillCartLimitIssue`: the key at fault and how to describe
 * its authored value. A `Record` so a new issue does not compile until it has
 * a report.
 */
const PREFILL_CART_LIMIT_ISSUE_REPORTS: Record<
  PrefillCartLimitIssue,
  {
    field: 'limit' | 'sendLimit'
    describe: (authored: string | null) => string
  }
> = {
  clamped: {
    field: 'limit',
    describe: (authored) =>
      `${authored} is above ${PREFILL_CART_MAX_LIMIT}; ${PREFILL_CART_MAX_LIMIT} would be sent instead`,
  },
  rejected: {
    field: 'limit',
    describe: (authored) =>
      `${authored} is not a positive integer; no limit is sent, so the server applies its own default`,
  },
  flagNotBoolean: {
    field: 'sendLimit',
    describe: (authored) =>
      `${authored} is not a boolean; the limit is not sent`,
  },
  missingLimit: {
    field: 'limit',
    describe: () =>
      'is not authored but sendLimit is on; no limit is sent, so the server applies its own default',
  },
}

export const fetchConfig = createAsyncThunk<
  IConfig,
  IGetConfigArgs,
  { state: { defaultSlice: ISliceDefault } }
>(
  'defaultSlice/fetchData',
  async (
    { slug, language },
    { rejectWithValue, dispatch, getState, requestId },
  ) => {
    /*
     * Every failed load ends on the error screen with the boot loader removed,
     * and the loader is removed at most once per load. Removing `STORE_CONFIG`
     * a second time would not be harmless: `removeLoader` does not guard a
     * missing name, and `splice(-1, 1)` drops whichever loader is last.
     *
     * Two paths could otherwise remove it twice. A request failure reaches
     * this function twice — `getConfig` calls it as its failure callback and
     * then rejects into the `catch` below — hence `bootFailureShown`. And the
     * success path's own removal can throw *after* its reducer has run: under
     * the legacy `ReactDOM.render` root a dispatch outside a React batch can
     * render synchronously from the store subscription, and a render error
     * propagates out of `dispatch` into the same `catch` — hence
     * `bootLoaderRemoved`. The error screen is still raised in that case: the
     * thunk rejects, so the config never reaches the store, and the reload
     * callback is the only way forward.
     *
     * Only the newest load may end on the error screen. Config.tsx starts a
     * load per slug change without cancelling the previous one, so an older
     * load that fails after a newer one succeeded would otherwise put the
     * error screen over a working storefront. A superseded load still removes
     * the loader it added, under the same once-only flags.
     */
    const isSuperseded = () =>
      isSupersededConfigLoad(getState().defaultSlice, requestId)
    // Tags a superseded load's Datadog reports with its own slug, because the
    // context's storefront is read from `window.location` and names the newer
    // load by then. A load that is still the newest adds nothing, so its
    // reports are unchanged.
    const supersededInfo = () =>
      isSuperseded() ? { superseded: true, slug } : {}
    let bootFailureShown = false
    let bootLoaderRemoved = false
    const showBootFailure = () => {
      if (bootFailureShown) {
        return
      }
      bootFailureShown = true
      if (!isSuperseded()) {
        dispatch(
          manageErrors({
            errorMessageTranslation: ErrorMessageTranslation.ErrorOnInitialLoad,
            globalError: true,
            showErrorTitle: true,
            onErrorCallback: () => window.location.reload(),
          }),
        )
      }
      if (!bootLoaderRemoved) {
        dispatch(removeLoader(LoadersType.STORE_CONFIG))
      }
    }
    try {
      dispatch(addLoader(LoadersType.STORE_CONFIG))
      dispatch(setStoreName(slug))
      const { data } = await getConfig(slug, showBootFailure)
      const config = processConfig(data, language, supersededInfo())
      if (isHidePricesMisconfigured(config.options?.hidePrices)) {
        datadogRum.addError(
          new Error(
            'hidePrices enabled but misconfigured (missing customField/flagValue); feature inactive',
          ),
          generateDataDogContext({
            title: 'hidePrices misconfigured',
            extraInfo: { function: 'fetchConfig', ...supersededInfo() },
          }),
        )
      }
      // Against `data`, the authored config, not `config`. `processConfig`
      // corrects these two values, so checking its output would only ever see
      // a valid one and this report could never fire. The `hidePrices` and
      // `packages.sort` checks read fields nothing rewrites, which is why they
      // can take the processed config safely.
      if (
        isRecommendationLimitsMisconfigured(data?.globals?.recommendedProducts)
      ) {
        datadogRum.addError(
          new Error(
            'globals.recommendedProducts carouselLimit/listLimit is not a positive integer; the shipped default is used instead',
          ),
          generateDataDogContext({
            title: 'recommendation limits misconfigured',
            extraInfo: { function: 'fetchConfig', ...supersededInfo() },
          }),
        )
      }
      // Also against `data`, so an omitted limit (not a misconfiguration)
      // stays distinguishable from a bad one even if a default is ever shipped
      // under `globals.prefillCart` again.
      const authoredPrefillCart = data?.globals?.prefillCart
      const prefillCartLimitIssue =
        getPrefillCartLimitIssue(authoredPrefillCart)
      if (prefillCartLimitIssue) {
        const { field, describe } =
          PREFILL_CART_LIMIT_ISSUE_REPORTS[prefillCartLimitIssue]
        // Live fallback: on `missingLimit` the key is absent and
        // `JSON.stringify(undefined)` is `undefined`, which would drop the
        // facet rather than say the value was absent.
        const authored = JSON.stringify(authoredPrefillCart?.[field]) ?? null
        datadogRum.addError(
          new Error(`globals.prefillCart.${field} ${describe(authored)}`),
          generateDataDogContext({
            title: `prefillCart.${field} ${prefillCartLimitIssue}`,
            extraInfo: {
              function: 'fetchConfig',
              reason: prefillCartLimitIssue,
              field,
              authored,
              ...supersededInfo(),
            },
          }),
        )
      }
      if (isPackageSortMisconfigured(config.globals?.packages?.sort)) {
        datadogRum.addError(
          new Error(
            'packages.sort enabled but misconfigured (unrecognised field/direction/enabled); packages keep catalog order',
          ),
          generateDataDogContext({
            title: 'packages.sort misconfigured',
            extraInfo: { function: 'fetchConfig', ...supersededInfo() },
          }),
        )
      }
      // Also reads the processed config: `variant` is a literal string prop
      // the author sets and `processConfig` does not rewrite it, so the
      // processed and authored values agree. One Datadog error per misplaced
      // instance rather than one per load, because a tenant that drops two
      // summary panels into two containers has two things to fix. See
      // COM-901 and `utils/cartCheckoutSummaryPlacement.ts`.
      findMisplacedCartCheckoutSummaries(config.containers).forEach(
        ({ containerKey, section, uid }) => {
          datadogRum.addError(
            new Error(
              `CartCheckoutPanel variant='summary' at containers.${containerKey}.${section} ("${uid}") has no sibling SummaryList; the panel renders chromeless on the page background`,
            ),
            generateDataDogContext({
              title: 'CartCheckoutPanel summary variant misplaced',
              extraInfo: {
                function: 'fetchConfig',
                containerKey,
                section,
                uid,
                ...supersededInfo(),
              },
            }),
          )
        },
      )
      if (isSuperseded()) {
        /*
         * A newer load owns the store: its `setModals` must stand, and the
         * reducer drops this load's `fulfilled`. The misconfiguration reports
         * above still went out, tagged with this load's slug, since the
         * config is this storefront's whether or not the shopper stayed. The
         * theme cache is keyed by this load's own slug, so writing it is
         * still correct.
         */
        writeCachedTheme(slug, config.theme)
        bootLoaderRemoved = true
        dispatch(removeLoader(LoadersType.STORE_CONFIG))
        return config
      }
      const modals: IModals = {}
      Object.keys(config.modals || {}).forEach((modalKey) => {
        modals[modalKey] = {
          show: false,
          state: {},
        }
      })
      dispatch(setModals(modals))
      /*
       * Remember the palette so the next cold open on this storefront paints
       * branded on its first frame rather than neutral (COM-1110). This is the
       * only place the resolved theme exists beside the slug it belongs to, and
       * `extraReducers` cannot do it because a reducer has to stay pure.
       * `writeCachedTheme` swallows its own storage errors, so nothing here
       * depends on it succeeding.
       */
      writeCachedTheme(slug, config.theme)
      // Set before the dispatch, not after it: a throw out of `dispatch` comes
      // from a subscriber, after the reducer has already removed the loader,
      // so a flag set on the next line would never be set when it matters.
      bootLoaderRemoved = true
      dispatch(removeLoader(LoadersType.STORE_CONFIG))
      return config
    } catch (error) {
      /*
       * A superseded failure is still reported, since it is a real failure
       * of this storefront's config, but tagged with its own slug because the
       * context's storefront is the newer one's. It does not `startView`:
       * that would rename the RUM view the newer load is running in.
       */
      const superseded = isSuperseded()
      const context = generateDataDogContext({
        title: 'could not load config',
        extraInfo: { function: 'fetchConfig', ...supersededInfo() },
      })
      if (!superseded) {
        datadogRum.startView(context.viewName)
      }
      datadogRum.addError(error, context)
      // Before this, a throw after the request succeeded (in `processConfig`,
      // say) rejected with the loader still up and no error set: a splash that
      // never cleared and nothing to retry from.
      showBootFailure()
      return rejectWithValue(error)
    }
  },
)

export default defaultSlice.reducer
